/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable no-plusplus */
import { diff, applyChange, revertChange } from 'deep-diff'
import type { PreFilter, Diff } from 'deep-diff'
import { cloneDeep } from 'lodash-es'

interface Fn<T> {
  (...args: any[]): T
}

type State<T> = Fn<T> | T

export type DiffState<T> = Diff<{ value: T }>[]

export interface RecorderOptions<T> {
  prefilter?: PreFilter<T>
  max?: number
}

export class HistoryRecorder<T> {
  history: T[]
  current: number

  constructor(public max = 20, history?: T[]) {
    this.history = history || []
    this.current = -1
  }

  // 添加一个操作记录
  push(state: T) {
    // 删除当前位置之后的所有记录
    this.history.splice(
      this.current + 1,
      this.history.length - this.current - 1,
    )

    // 添加新记录
    this.history.push(state)
    if (this.history.length > this.max) {
      this.history.shift()
    } else {
      this.current++
    }
  }

  // 撤销上一步操作
  undo() {
    if (this.current >= 0) {
      this.current--
      return this.history[this.current + 1]
    }
    return null
  }

  // 重做上一步撤销的操作
  redo() {
    if (this.current < this.history.length - 1) {
      this.current++
      return this.history[this.current]
    }
    return null
  }

  // 获取当前操作记录
  getCurrentState() {
    if (this.current > -1) {
      return this.history[this.current]
    }
    return null
  }

  reset() {
    this.history = []
    this.current = -1
  }
}

export class HistoryStateRecorder<T> extends HistoryRecorder<DiffState<T>> {
  savedState: T
  options: RecorderOptions<T>

  constructor(private state: State<T>, options: RecorderOptions<T>) {
    super(options.max)
    this.options = options
    this.savedState = this.save()
  }

  save() {
    this.savedState = cloneDeep(this._getState())
    return this.savedState
  }

  _getState(): T {
    const state = this.state as any
    return typeof state === 'function' ? state() : state
  }

  push() {
    const value = this._getState()
    const change = diff(
      { value: this.savedState },
      { value: cloneDeep(value) },
      this.options.prefilter as any,
    )

    if (change) {
      this.savedState = cloneDeep(value)
      super.push(change)
    }

    return change
  }

  undo() {
    const state = this._getState()
    const change = super.undo()
    if (change) {
      const cloned = cloneDeep(state)
      change.forEach((item) => {
        revertChange({ value: cloned }, { value: this.savedState }, item)
      })

      this.savedState = cloned
    }

    return change
  }

  redo() {
    const state = this._getState()
    const change = super.redo()
    if (change) {
      const cloned = cloneDeep(state)
      change.forEach((item) => {
        applyChange({ value: cloned }, { value: this.savedState }, item)
      })

      this.savedState = cloned
    }

    return change
  }
}
